import { useState } from "react";
import clsx from "clsx";

import type { IndividualSchema, FtePeriodSchema, AbsenceAllowanceSchema } from "@nexttrack/api-schema";
import { DepartmentsPage } from "@/pages/departments-page";
import {
    useGetIndividualsQuery,
    useCreateIndividualMutation,
    useUpdateIndividualMutation,
    useArchiveIndividualMutation,
    useAddFtePeriodMutation,
    useUpdateFtePeriodMutation,
    useDeleteFtePeriodMutation,
    useAddAbsenceAllowanceMutation,
    useUpdateAbsenceAllowanceMutation,
    useDeleteAbsenceAllowanceMutation,
} from "@/store/services/individuals/individuals.service";
import { useGetDepartmentsQuery } from "@/store/services/departments/departments.service";
import { useGetTeamsQuery } from "@/store/services/teams/teams.service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const ABSENCE_TYPES = ["vacation", "sick_leave", "parental_leave", "sabbatical"] as const;

export const IndividualsPage = () => {
    const [filterDeptId, setFilterDeptId] = useState("all");
    const [filterTeamId, setFilterTeamId] = useState("all");
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const { data: departments } = useGetDepartmentsQuery();
    const { data: teams } = useGetTeamsQuery(filterDeptId !== "all" ? { departmentId: filterDeptId } : undefined);
    const { data: individuals, isLoading } = useGetIndividualsQuery({
        departmentId: filterDeptId !== "all" ? filterDeptId : undefined,
        teamId: filterTeamId !== "all" ? filterTeamId : undefined,
    });

    const [createIndividual, { isLoading: isCreating }] = useCreateIndividualMutation();
    const [updateIndividual, { isLoading: isUpdating }] = useUpdateIndividualMutation();
    const [archiveIndividual] = useArchiveIndividualMutation();
    const [addFtePeriod] = useAddFtePeriodMutation();
    const [updateFtePeriod] = useUpdateFtePeriodMutation();
    const [deleteFtePeriod] = useDeleteFtePeriodMutation();
    const [addAbsenceAllowance] = useAddAbsenceAllowanceMutation();
    const [updateAbsenceAllowance] = useUpdateAbsenceAllowanceMutation();
    const [deleteAbsenceAllowance] = useDeleteAbsenceAllowanceMutation();

    // All teams for dropdowns (unfiltered)
    const { data: allTeams } = useGetTeamsQuery();

    // New individual dialog
    const [newOpen, setNewOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [newTeamId, setNewTeamId] = useState("");
    const [newFteValue, setNewFteValue] = useState("1");
    const [newFteStartDate, setNewFteStartDate] = useState(() => new Date().toISOString().slice(0, 10));

    // Edit individual dialog
    const [editTarget, setEditTarget] = useState<IndividualSchema | null>(null);
    const [editName, setEditName] = useState("");
    const [editTeamId, setEditTeamId] = useState("");

    // Archive individual dialog
    const [archiveTarget, setArchiveTarget] = useState<IndividualSchema | null>(null);
    const [archiveDateInput, setArchiveDateInput] = useState(() => new Date().toISOString().slice(0, 10));

    // FTE period dialogs
    const [ftePeriodOpen, setFtePeriodOpen] = useState(false);
    const [ftePeriodTarget, setFtePeriodTarget] = useState<{ individualId: string; period?: FtePeriodSchema } | null>(
        null,
    );
    const [fteStartDate, setFteStartDate] = useState("");
    const [fteValue, setFteValue] = useState("1");

    // Absence allowance dialogs
    const [absenceOpen, setAbsenceOpen] = useState(false);
    const [absenceTarget, setAbsenceTarget] = useState<{
        individualId: string;
        allowance?: AbsenceAllowanceSchema;
    } | null>(null);
    const [absenceType, setAbsenceType] = useState<(typeof ABSENCE_TYPES)[number]>("vacation");
    const [absenceDays, setAbsenceDays] = useState("0");
    const [absenceStartDate, setAbsenceStartDate] = useState(() => new Date().toISOString().slice(0, 10));

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newName.trim() || !newTeamId) return;
        await createIndividual({
            name: newName.trim(),
            teamId: newTeamId,
            initialFteValue: parseFloat(newFteValue),
            initialFteStartDate: newFteStartDate,
        });
        setNewName("");
        setNewTeamId("");
        setNewFteValue("1");
        setNewFteStartDate(new Date().toISOString().slice(0, 10));
        setNewOpen(false);
    };

    const openEdit = (ind: IndividualSchema) => {
        setEditTarget(ind);
        setEditName(ind.name);
        setEditTeamId(ind.teamId);
    };

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editTarget) return;
        await updateIndividual({ id: editTarget.id, data: { name: editName.trim(), teamId: editTeamId } });
        setEditTarget(null);
    };

    const openArchiveDialog = (ind: IndividualSchema) => {
        setArchiveTarget(ind);
        setArchiveDateInput(new Date().toISOString().slice(0, 10));
    };

    const handleArchive = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!archiveTarget) return;
        await archiveIndividual({ id: archiveTarget.id, archiveDate: archiveDateInput });
        if (expandedId === archiveTarget.id) setExpandedId(null);
        setArchiveTarget(null);
    };

    const openFtePeriod = (individualId: string, period?: FtePeriodSchema) => {
        setFtePeriodTarget({ individualId, period });
        setFteStartDate(period?.startDate ?? new Date().toISOString().slice(0, 10));
        setFteValue(period ? String(period.fteValue) : "");
        setFtePeriodOpen(true);
    };

    const handleFtePeriodSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!ftePeriodTarget) return;
        const data = { startDate: fteStartDate, fteValue: parseFloat(fteValue) };
        if (ftePeriodTarget.period) {
            await updateFtePeriod({
                individualId: ftePeriodTarget.individualId,
                ftePeriodId: ftePeriodTarget.period.id,
                data,
            });
        } else {
            await addFtePeriod({ individualId: ftePeriodTarget.individualId, data });
        }
        setFtePeriodOpen(false);
        setFtePeriodTarget(null);
    };

    const handleDeleteFtePeriod = async (individualId: string, ftePeriodId: string) => {
        if (!confirm("Delete this FTE period?")) return;
        await deleteFtePeriod({ individualId, ftePeriodId });
    };

    const openAbsence = (individualId: string, allowance?: AbsenceAllowanceSchema) => {
        setAbsenceTarget({ individualId, allowance });
        setAbsenceType(allowance?.type ?? "vacation");
        setAbsenceDays(String(allowance?.remainingDays ?? 0));
        setAbsenceStartDate(allowance?.startDate ?? new Date().toISOString().slice(0, 10));
        setAbsenceOpen(true);
    };

    const handleAbsenceSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!absenceTarget) return;
        const data = { type: absenceType, remainingDays: parseInt(absenceDays), startDate: absenceStartDate };
        if (absenceTarget.allowance) {
            await updateAbsenceAllowance({
                individualId: absenceTarget.individualId,
                allowanceId: absenceTarget.allowance.id,
                data,
            });
        } else {
            await addAbsenceAllowance({ individualId: absenceTarget.individualId, data });
        }
        setAbsenceOpen(false);
        setAbsenceTarget(null);
    };

    const handleDeleteAbsence = async (individualId: string, allowanceId: string) => {
        if (!confirm("Delete this absence allowance?")) return;
        await deleteAbsenceAllowance({ individualId, allowanceId });
    };

    const teamById = Object.fromEntries((allTeams ?? []).map((t) => [t.id, t]));

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">People</h1>
                <Button onClick={() => setNewOpen(true)}>New Individual</Button>
            </div>

            {/* Filters */}
            <div className="flex gap-3 flex-wrap">
                <Select
                    value={filterDeptId}
                    onValueChange={(v) => {
                        setFilterDeptId(v);
                        setFilterTeamId("all");
                    }}
                >
                    <SelectTrigger className="w-44">
                        <SelectValue placeholder="Department" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Departments</SelectItem>
                        {departments?.map((d) => (
                            <SelectItem key={d.id} value={d.id}>
                                {d.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={filterTeamId} onValueChange={setFilterTeamId}>
                    <SelectTrigger className="w-40">
                        <SelectValue placeholder="Team" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Teams</SelectItem>
                        {teams?.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                                {t.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {isLoading ? (
                <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-10 w-full" />
                    ))}
                </div>
            ) : (
                <div className="border rounded-lg overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Team</TableHead>
                                <TableHead>FTE</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {individuals?.map((ind) => {
                                const latestFte = [...ind.ftePeriods].sort((a, b) =>
                                    b.startDate.localeCompare(a.startDate),
                                )[0];
                                const team = teamById[ind.teamId];
                                const isExpanded = expandedId === ind.id;
                                return (
                                    <>
                                        <TableRow
                                            key={ind.id}
                                            className={clsx("cursor-pointer", isExpanded && "bg-slate-50")}
                                            onClick={() => setExpandedId(isExpanded ? null : ind.id)}
                                        >
                                            <TableCell className="font-medium">
                                                <span
                                                    className={clsx(ind.archiveDate && "line-through text-slate-400")}
                                                >
                                                    {ind.name}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-slate-500">{team?.name ?? ind.teamId}</TableCell>
                                            <TableCell>{latestFte ? latestFte.fteValue.toFixed(2) : "—"}</TableCell>
                                            <TableCell>
                                                {ind.archiveDate ? (
                                                    <Badge variant="secondary">Archived from {ind.archiveDate}</Badge>
                                                ) : (
                                                    <Badge variant="outline">Active</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell
                                                className="text-right space-x-2"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Button variant="outline" size="sm" onClick={() => openEdit(ind)}>
                                                    Edit
                                                </Button>
                                                {!ind.archiveDate && (
                                                    <Button
                                                        variant="destructive"
                                                        size="sm"
                                                        onClick={() => openArchiveDialog(ind)}
                                                    >
                                                        Archive
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        {isExpanded && (
                                            <TableRow key={`${ind.id}-expanded`}>
                                                <TableCell colSpan={5} className="bg-slate-50 p-4">
                                                    <div className="space-y-4">
                                                        {/* FTE Periods */}
                                                        <div>
                                                            <div className="flex items-center justify-between mb-2">
                                                                <span className="text-sm font-semibold">
                                                                    FTE Periods
                                                                </span>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => openFtePeriod(ind.id)}
                                                                >
                                                                    Add FTE Period
                                                                </Button>
                                                            </div>
                                                            {ind.ftePeriods.length === 0 ? (
                                                                <p className="text-xs text-slate-400">No FTE periods</p>
                                                            ) : (
                                                                <table className="text-xs w-full">
                                                                    <thead>
                                                                        <tr className="text-slate-500">
                                                                            <th className="text-left pr-4">
                                                                                Start Date
                                                                            </th>
                                                                            <th className="text-left pr-4">End Date</th>
                                                                            <th className="text-left pr-4">FTE</th>
                                                                            <th />
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(() => {
                                                                            const sorted = ind.ftePeriods
                                                                                .slice()
                                                                                .sort((a, b) =>
                                                                                    b.startDate.localeCompare(
                                                                                        a.startDate,
                                                                                    ),
                                                                                );
                                                                            return sorted.map((fp, idx) => {
                                                                                const nextPeriod = sorted[idx - 1];
                                                                                const endDate = nextPeriod
                                                                                    ? new Date(
                                                                                          new Date(
                                                                                              nextPeriod.startDate,
                                                                                          ).getTime() - 86400000,
                                                                                      )
                                                                                          .toISOString()
                                                                                          .slice(0, 10)
                                                                                    : null;
                                                                                return (
                                                                                    <tr key={fp.id}>
                                                                                        <td className="pr-4 py-1">
                                                                                            {fp.startDate}
                                                                                        </td>
                                                                                        <td className="pr-4 py-1 text-slate-400">
                                                                                            {endDate ?? "present"}
                                                                                        </td>
                                                                                        <td className="pr-4">
                                                                                            {fp.fteValue.toFixed(2)}
                                                                                        </td>
                                                                                        <td className="space-x-1">
                                                                                            <Button
                                                                                                variant="ghost"
                                                                                                size="sm"
                                                                                                onClick={() =>
                                                                                                    openFtePeriod(
                                                                                                        ind.id,
                                                                                                        fp,
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                Edit
                                                                                            </Button>
                                                                                            <Button
                                                                                                variant="ghost"
                                                                                                size="sm"
                                                                                                className="text-red-500"
                                                                                                onClick={() =>
                                                                                                    handleDeleteFtePeriod(
                                                                                                        ind.id,
                                                                                                        fp.id,
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                Del
                                                                                            </Button>
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            });
                                                                        })()}
                                                                    </tbody>
                                                                </table>
                                                            )}
                                                        </div>
                                                        <Separator />
                                                        {/* Absence Allowances */}
                                                        <div>
                                                            <div className="flex items-center justify-between mb-2">
                                                                <span className="text-sm font-semibold">
                                                                    Absence Allowances
                                                                </span>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => openAbsence(ind.id)}
                                                                >
                                                                    Add Allowance
                                                                </Button>
                                                            </div>
                                                            {ind.absenceAllowances.length === 0 ? (
                                                                <p className="text-xs text-slate-400">
                                                                    No absence allowances
                                                                </p>
                                                            ) : (
                                                                <table className="text-xs w-full">
                                                                    <thead>
                                                                        <tr className="text-slate-500">
                                                                            <th className="text-left pr-4">Type</th>
                                                                            <th className="text-left pr-4">Days</th>
                                                                            <th className="text-left pr-4">
                                                                                Start Date
                                                                            </th>
                                                                            <th className="text-left pr-4">End Date</th>
                                                                            <th />
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {ind.absenceAllowances.map((aa) => (
                                                                            <tr key={aa.id}>
                                                                                <td className="pr-4 py-1 capitalize">
                                                                                    {aa.type.replace("_", " ")}
                                                                                </td>
                                                                                <td className="pr-4">
                                                                                    {aa.remainingDays}
                                                                                </td>
                                                                                <td className="pr-4">{aa.startDate}</td>
                                                                                <td className="pr-4">
                                                                                    {`${aa.startDate.slice(0, 4)}-12-31`}
                                                                                </td>
                                                                                <td className="space-x-1">
                                                                                    <Button
                                                                                        variant="ghost"
                                                                                        size="sm"
                                                                                        onClick={() =>
                                                                                            openAbsence(ind.id, aa)
                                                                                        }
                                                                                    >
                                                                                        Edit
                                                                                    </Button>
                                                                                    <Button
                                                                                        variant="ghost"
                                                                                        size="sm"
                                                                                        className="text-red-500"
                                                                                        onClick={() =>
                                                                                            handleDeleteAbsence(
                                                                                                ind.id,
                                                                                                aa.id,
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        Del
                                                                                    </Button>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            )}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </>
                                );
                            })}
                            {(!individuals || individuals.length === 0) && (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center text-slate-400 py-8">
                                        No individuals found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            <Separator />
            <DepartmentsPage />

            {/* New Individual Dialog */}
            <Dialog open={newOpen} onOpenChange={setNewOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>New Individual</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Name</label>
                            <Input value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Team</label>
                            <Select value={newTeamId} onValueChange={setNewTeamId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select team" />
                                </SelectTrigger>
                                <SelectContent>
                                    {allTeams?.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Initial FTE (0–1)</label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max="1"
                                value={newFteValue}
                                onChange={(e) => setNewFteValue(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">FTE Start Date</label>
                            <Input
                                type="date"
                                value={newFteStartDate}
                                onChange={(e) => setNewFteStartDate(e.target.value)}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setNewOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isCreating || !newName.trim() || !newTeamId}>
                                {isCreating ? "Creating..." : "Create"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit Individual Dialog */}
            <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Individual</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUpdate} className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Name</label>
                            <Input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Team</label>
                            <Select value={editTeamId} onValueChange={setEditTeamId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select team" />
                                </SelectTrigger>
                                <SelectContent>
                                    {allTeams?.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isUpdating || !editName.trim()}>
                                {isUpdating ? "Saving..." : "Save"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Archive Individual Dialog */}
            <Dialog open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Archive {archiveTarget?.name}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleArchive} className="space-y-4">
                        <p className="text-sm text-slate-500">
                            Bookings after this date will be removed or truncated. The individual&apos;s capacity will
                            be excluded from the timeline from this date onwards.
                        </p>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Archive from date</label>
                            <Input
                                type="date"
                                value={archiveDateInput}
                                onChange={(e) => setArchiveDateInput(e.target.value)}
                                autoFocus
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setArchiveTarget(null)}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="destructive" disabled={!archiveDateInput}>
                                Archive
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* FTE Period Dialog */}
            <Dialog
                open={ftePeriodOpen}
                onOpenChange={(open) => {
                    setFtePeriodOpen(open);
                    if (!open) setFtePeriodTarget(null);
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{ftePeriodTarget?.period ? "Edit FTE Period" : "Add FTE Period"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleFtePeriodSave} className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Start Date</label>
                            <Input type="date" value={fteStartDate} onChange={(e) => setFteStartDate(e.target.value)} />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">FTE Value (0–1)</label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max="1"
                                value={fteValue}
                                onChange={(e) => setFteValue(e.target.value)}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setFtePeriodOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit">Save</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Absence Allowance Dialog */}
            <Dialog
                open={absenceOpen}
                onOpenChange={(open) => {
                    setAbsenceOpen(open);
                    if (!open) setAbsenceTarget(null);
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {absenceTarget?.allowance ? "Edit Absence Allowance" : "Add Absence Allowance"}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAbsenceSave} className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Type</label>
                            <Select
                                value={absenceType}
                                onValueChange={(v) => setAbsenceType(v as (typeof ABSENCE_TYPES)[number])}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ABSENCE_TYPES.map((t) => (
                                        <SelectItem key={t} value={t}>
                                            {t.replace("_", " ")}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Remaining Days</label>
                            <Input
                                type="number"
                                min="0"
                                value={absenceDays}
                                onChange={(e) => setAbsenceDays(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Start Date</label>
                            <Input
                                type="date"
                                value={absenceStartDate}
                                onChange={(e) => setAbsenceStartDate(e.target.value)}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setAbsenceOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit">Save</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
